import { z } from 'zod'
import { decimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { invoiceStatusSchema } from './invoices.ts'
import { orderedSupplyStatusSchema } from './warehouse.ts'

export const dashboardShortageSchema = z.object({
  orderId: uuidSchema,
  orderNumber: z.string(),
  productName: z.string(),
  /** Null for older orders planned as a whole rather than per item. */
  orderItemId: uuidSchema.nullable(),
  itemDescription: z.string().nullable(),
  materialId: uuidSchema,
  materialName: z.string(),
  materialGrade: z.string().nullable(),
  unit: z.string(),
  shortage: decimalSchema,
  toOrder: decimalSchema,
})

export const dashboardOverdueApprovalSchema = z.object({
  id: uuidSchema,
  supplierName: z.string(),
  purpose: z.string(),
  amount: decimalSchema,
  dueAt: timestampSchema,
})

export const dashboardExpectedReceiptSchema = z.object({
  id: uuidSchema,
  source: z.enum(['warehouse', 'foreign']),
  supplierName: z.string(),
  materialName: z.string().nullable(),
  quantity: decimalSchema.nullable(),
  unit: z.string().nullable(),
  expectedAt: z.iso.date(),
  status: orderedSupplyStatusSchema,
})

export const dashboardUnpaidInvoiceSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  clientName: z.string(),
  remainingUah: decimalSchema,
  dueAt: timestampSchema.nullable(),
  effectiveStatus: invoiceStatusSchema,
})

export type DashboardShortage = z.infer<typeof dashboardShortageSchema>
export type DashboardOverdueApproval = z.infer<typeof dashboardOverdueApprovalSchema>
export type DashboardExpectedReceipt = z.infer<typeof dashboardExpectedReceiptSchema>
export type DashboardUnpaidInvoice = z.infer<typeof dashboardUnpaidInvoiceSchema>

const section = <T extends z.ZodType>(item: T) =>
  z.object({ total: z.int().nonnegative(), items: z.array(item) })

export const dashboardOverviewSchema = z.object({
  generatedAt: timestampSchema,
  shortages: section(dashboardShortageSchema).nullable(),
  overdueApprovals: section(dashboardOverdueApprovalSchema).nullable(),
  expectedReceipts: section(dashboardExpectedReceiptSchema).nullable(),
  unpaidInvoices: section(dashboardUnpaidInvoiceSchema).nullable(),
})

export type DashboardOverview = z.infer<typeof dashboardOverviewSchema>
